/**
 * Tenant View
 * 
 * Multi-tenant management for eStream platform:
 * - Tenant overview and details
 * - Usage metrics and quotas
 * - Team member management
 * - API key management
 * - Billing and subscription info
 */

import { useState, useEffect } from 'react';
import { useDebug } from '../context/DebugContext';
import './Tenants.css';

// SVG Icons
const Icons = {
  Building: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22V12h6v10M9 6h.01M15 6h.01M9 10h.01M15 10h.01" />
    </svg>
  ),
  Users: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  Key: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  ),
  Activity: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  CreditCard: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 4v12M4 10h12" />
    </svg>
  ),
  Copy: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5" y="5" width="9" height="9" rx="1" />
      <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h12M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M13 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V4" />
    </svg>
  ),
};

interface Tenant {
  id: string;
  name: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'suspended' | 'trial';
  createdAt: string;
  usage: {
    events: number;
    eventsLimit: number;
    storage: number;
    storageLimit: number;
    nodes: number;
    nodesLimit: number;
  };
}

interface TenantEconomics {
  transactionsToday: number;
  transactionsTotal: number;
  tokenVolume24h: number;
  activeAccounts: number;
  feesPaid: number;
  feesPaidUsd: number;
  networkShare: {
    transactions: number;
    volume: number;
    accounts: number;
  };
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'developer' | 'viewer';
  joinedAt: string;
  lastActive: string;
}

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string;
  expiresAt?: string;
}

type TabId = 'overview' | 'team' | 'api-keys' | 'usage' | 'billing';

export default function Tenants() {
  const { addEvent } = useDebug();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [economics, setEconomics] = useState<TenantEconomics | null>(null);
  
  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Tenant page',
    });
    
    fetchTenantData();
  }, [addEvent]);
  
  const fetchTenantData = async () => {
    setLoading(true);
    
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      
      // Fetch tenant info
      const tenantResponse = await fetch(`${edgeUrl}/api/tenant`);
      if (tenantResponse.ok) {
        const data = await tenantResponse.json();
        setTenant(data.tenant);
        setTeamMembers(data.team || []);
        setApiKeys(data.apiKeys || []);
      } else {
        // Mock data for development
        setMockData();
      }
      
      // Fetch tenant economics (computed by lattice circuits)
      const economicsResponse = await fetch(`${edgeUrl}/api/tenant/economics`);
      if (economicsResponse.ok) {
        const econ = await economicsResponse.json();
        setEconomics(econ);
      } else {
        setMockEconomics();
      }
    } catch {
      // Mock data for development
      setMockData();
      setMockEconomics();
    } finally {
      setLoading(false);
    }
  };
  
  const setMockEconomics = () => {
    setEconomics({
      transactionsToday: 52_341,
      transactionsTotal: 2_456_789,
      tokenVolume24h: 625_000,
      activeAccounts: 2_847,
      feesPaid: 15_234,
      feesPaidUsd: 37_628,
      networkShare: {
        transactions: 4.2,
        volume: 3.8,
        accounts: 5.1,
      },
    });
  };
  
  const setMockData = () => {
    setTenant({
      id: 'tenant-alpha-001',
      name: 'Alpha Devnet Organization',
      plan: 'professional',
      status: 'active',
      createdAt: '2025-06-15T10:00:00Z',
      usage: {
        events: 1_234_567,
        eventsLimit: 10_000_000,
        storage: 2.5,
        storageLimit: 10,
        nodes: 4,
        nodesLimit: 10,
      },
    });
    
    setTeamMembers([
      { id: 'u1', email: 'alice@example.com', name: 'Alice Chen', role: 'owner', joinedAt: '2025-06-15T10:00:00Z', lastActive: new Date().toISOString() },
      { id: 'u2', email: 'bob@example.com', name: 'Bob Smith', role: 'admin', joinedAt: '2025-07-01T10:00:00Z', lastActive: new Date(Date.now() - 3600000).toISOString() },
      { id: 'u3', email: 'charlie@example.com', name: 'Charlie Davis', role: 'developer', joinedAt: '2025-08-15T10:00:00Z', lastActive: new Date(Date.now() - 86400000).toISOString() },
    ]);
    
    setApiKeys([
      { id: 'key1', name: 'Production API', prefix: 'esk_live_abc', permissions: ['read', 'write'], createdAt: '2025-07-01T10:00:00Z', lastUsed: new Date().toISOString() },
      { id: 'key2', name: 'Development', prefix: 'esk_test_xyz', permissions: ['read', 'write', 'admin'], createdAt: '2025-08-01T10:00:00Z', lastUsed: new Date(Date.now() - 3600000).toISOString() },
      { id: 'key3', name: 'CI/CD Pipeline', prefix: 'esk_ci_123', permissions: ['read'], createdAt: '2025-09-01T10:00:00Z', lastUsed: new Date(Date.now() - 86400000).toISOString(), expiresAt: '2026-09-01T10:00:00Z' },
    ]);
  };
  
  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    addEvent({
      source: 'console',
      category: 'info',
      message: 'Copied to clipboard',
    });
  };
  
  const formatNumber = (n: number) => n.toLocaleString();
  
  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };
  
  const getUsagePercentage = (used: number, limit: number) => Math.min(100, (used / limit) * 100);
  
  const getPlanBadgeClass = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'plan-enterprise';
      case 'professional': return 'plan-professional';
      default: return 'plan-starter';
    }
  };
  
  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'role-owner';
      case 'admin': return 'role-admin';
      case 'developer': return 'role-developer';
      default: return 'role-viewer';
    }
  };
  
  if (loading) {
    return (
      <div className="tenant-page loading-state">
        <div className="spinner" />
        <span>Loading tenant data...</span>
      </div>
    );
  }
  
  if (!tenant) {
    return (
      <div className="tenant-page error-state">
        <p>Failed to load tenant information.</p>
        <button className="btn-primary" onClick={fetchTenantData}>Retry</button>
      </div>
    );
  }
  
  return (
    <div className="tenant-page">
      {/* Header */}
      <div className="tenant-header">
        <div className="header-content">
          <div className="tenant-icon">
            <Icons.Building />
          </div>
          <div className="tenant-info">
            <h1>{tenant.name}</h1>
            <div className="tenant-meta">
              <span className={`plan-badge ${getPlanBadgeClass(tenant.plan)}`}>
                {tenant.plan}
              </span>
              <span className={`status-badge status-${tenant.status}`}>
                {tenant.status}
              </span>
              <span className="tenant-id">ID: {tenant.id}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="tab-nav">
        {(['overview', 'team', 'api-keys', 'usage', 'billing'] as TabId[]).map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'api-keys' ? 'API Keys' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="tenant-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            {/* Tenant Economics */}
            {economics && (
              <section className="card">
                <div className="card-header">
                  <h2><Icons.Activity /> Your Network Activity</h2>
                  <span className="network-share-badge">
                    {economics.networkShare.transactions.toFixed(1)}% of network
                  </span>
                </div>
                <div className="economics-grid">
                  <div className="econ-card">
                    <span className="econ-value">{formatNumber(economics.transactionsToday)}</span>
                    <span className="econ-label">Transactions Today</span>
                  </div>
                  <div className="econ-card">
                    <span className="econ-value">{formatNumber(economics.transactionsTotal)}</span>
                    <span className="econ-label">Total Transactions</span>
                  </div>
                  <div className="econ-card">
                    <span className="econ-value">${formatNumber(economics.tokenVolume24h)}</span>
                    <span className="econ-label">Token Volume (24h)</span>
                  </div>
                  <div className="econ-card">
                    <span className="econ-value">{formatNumber(economics.activeAccounts)}</span>
                    <span className="econ-label">Active Accounts</span>
                  </div>
                  <div className="econ-card">
                    <span className="econ-value">${formatNumber(economics.feesPaidUsd)}</span>
                    <span className="econ-label">Fees Paid (USD)</span>
                  </div>
                  <div className="econ-card highlight">
                    <span className="econ-value">{economics.networkShare.accounts.toFixed(1)}%</span>
                    <span className="econ-label">Network Share</span>
                  </div>
                </div>
              </section>
            )}
            
            {/* Usage Summary */}
            <section className="card">
              <div className="card-header">
                <h2><Icons.Activity /> Resource Usage</h2>
              </div>
              <div className="usage-grid">
                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">Events (this month)</span>
                    <span className="usage-value">
                      {formatNumber(tenant.usage.events)} / {formatNumber(tenant.usage.eventsLimit)}
                    </span>
                  </div>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill"
                      style={{ width: `${getUsagePercentage(tenant.usage.events, tenant.usage.eventsLimit)}%` }}
                    />
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">Storage</span>
                    <span className="usage-value">
                      {tenant.usage.storage} GB / {tenant.usage.storageLimit} GB
                    </span>
                  </div>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill"
                      style={{ width: `${getUsagePercentage(tenant.usage.storage, tenant.usage.storageLimit)}%` }}
                    />
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">Nodes</span>
                    <span className="usage-value">
                      {tenant.usage.nodes} / {tenant.usage.nodesLimit}
                    </span>
                  </div>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill"
                      style={{ width: `${getUsagePercentage(tenant.usage.nodes, tenant.usage.nodesLimit)}%` }}
                    />
                  </div>
                </div>
              </div>
            </section>
            
            {/* Quick Stats */}
            <div className="stats-grid">
              <div className="stat-card" onClick={() => setActiveTab('team')}>
                <div className="stat-icon"><Icons.Users /></div>
                <div className="stat-content">
                  <span className="stat-value">{teamMembers.length}</span>
                  <span className="stat-label">Team Members</span>
                </div>
              </div>
              
              <div className="stat-card" onClick={() => setActiveTab('api-keys')}>
                <div className="stat-icon"><Icons.Key /></div>
                <div className="stat-content">
                  <span className="stat-value">{apiKeys.length}</span>
                  <span className="stat-label">API Keys</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'team' && (
          <section className="card">
            <div className="card-header">
              <h2><Icons.Users /> Team Members</h2>
              <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
                <Icons.Plus /> Invite Member
              </button>
            </div>
            
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Last Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map(member => (
                  <tr key={member.id}>
                    <td>
                      <div className="member-cell">
                        <div className="member-avatar">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="member-info">
                          <span className="member-name">{member.name}</span>
                          <span className="member-email">{member.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(member.role)}`}>
                        {member.role}
                      </span>
                    </td>
                    <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                    <td>{formatTimeAgo(member.lastActive)}</td>
                    <td>
                      {member.role !== 'owner' && (
                        <button className="btn-sm btn-danger">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        
        {activeTab === 'api-keys' && (
          <section className="card">
            <div className="card-header">
              <h2><Icons.Key /> API Keys</h2>
              <button className="btn-primary" onClick={() => setShowCreateKeyModal(true)}>
                <Icons.Plus /> Create Key
              </button>
            </div>
            
            <div className="api-keys-list">
              {apiKeys.map(key => (
                <div key={key.id} className="api-key-card">
                  <div className="key-header">
                    <span className="key-name">{key.name}</span>
                    <div className="key-actions">
                      <button 
                        className="btn-icon" 
                        title="Copy key prefix"
                        onClick={() => copyToClipboard(key.prefix)}
                      >
                        <Icons.Copy />
                      </button>
                      <button className="btn-icon btn-danger" title="Revoke key">
                        <Icons.Trash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="key-info">
                    <code className="key-prefix">{key.prefix}•••••••</code>
                    <div className="key-permissions">
                      {key.permissions.map(perm => (
                        <span key={perm} className="permission-badge">{perm}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="key-meta">
                    <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                    <span>Last used: {formatTimeAgo(key.lastUsed)}</span>
                    {key.expiresAt && (
                      <span className="expires">Expires: {new Date(key.expiresAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {activeTab === 'usage' && (
          <section className="card">
            <div className="card-header">
              <h2><Icons.Activity /> Detailed Usage</h2>
            </div>
            <div className="usage-details">
              <p className="coming-soon">Detailed usage analytics coming soon.</p>
              <div className="usage-placeholder">
                <div className="chart-placeholder">
                  <span>Events over time chart</span>
                </div>
              </div>
            </div>
          </section>
        )}
        
        {activeTab === 'billing' && (
          <section className="card">
            <div className="card-header">
              <h2><Icons.CreditCard /> Billing</h2>
            </div>
            <div className="billing-content">
              <div className="current-plan">
                <h3>Current Plan</h3>
                <div className="plan-details">
                  <span className={`plan-badge large ${getPlanBadgeClass(tenant.plan)}`}>
                    {tenant.plan}
                  </span>
                  <p className="plan-description">
                    {tenant.plan === 'professional' 
                      ? '10M events/month, 10GB storage, 10 nodes, priority support'
                      : tenant.plan === 'enterprise'
                      ? 'Unlimited events, storage, and nodes with dedicated support'
                      : '1M events/month, 1GB storage, 3 nodes'}
                  </p>
                </div>
                <button className="btn-secondary">Upgrade Plan</button>
              </div>
              
              <div className="billing-history">
                <h3>Recent Invoices</h3>
                <p className="coming-soon">Billing history coming soon.</p>
              </div>
            </div>
          </section>
        )}
      </div>
      
      {/* Invite Modal */}
      {showInviteModal && (
        <InviteMemberModal onClose={() => setShowInviteModal(false)} />
      )}
      
      {/* Create Key Modal */}
      {showCreateKeyModal && (
        <CreateApiKeyModal onClose={() => setShowCreateKeyModal(false)} />
      )}
    </div>
  );
}

// Invite Member Modal
function InviteMemberModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'developer' | 'viewer'>('developer');
  const [submitting, setSubmitting] = useState(false);
  
  const handleInvite = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitting(false);
    onClose();
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Invite Team Member</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="colleague@example.com"
            />
          </div>
          
          <div className="form-group">
            <label>Role</label>
            <select value={role} onChange={e => setRole(e.target.value as any)}>
              <option value="admin">Admin - Full access except billing</option>
              <option value="developer">Developer - Deploy and manage circuits</option>
              <option value="viewer">Viewer - Read-only access</option>
            </select>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn-primary" 
            onClick={handleInvite}
            disabled={!email.trim() || submitting}
          >
            {submitting ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create API Key Modal
function CreateApiKeyModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>(['read']);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  const togglePermission = (perm: string) => {
    setPermissions(prev => 
      prev.includes(perm) 
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };
  
  const handleCreate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCreatedKey(`esk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`);
    setSubmitting(false);
  };
  
  const copyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{createdKey ? 'API Key Created' : 'Create API Key'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {createdKey ? (
          <div className="modal-body">
            <div className="key-created">
              <p className="warning-text">
                Copy this key now. You won't be able to see it again.
              </p>
              <div className="key-display">
                <code>{createdKey}</code>
                <button className="btn-icon" onClick={copyKey}>
                  <Icons.Copy />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-group">
              <label>Key Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Production API"
              />
            </div>
            
            <div className="form-group">
              <label>Permissions</label>
              <div className="permissions-grid">
                {['read', 'write', 'admin'].map(perm => (
                  <label key={perm} className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={permissions.includes(perm)}
                      onChange={() => togglePermission(perm)}
                    />
                    <span>{perm}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div className="modal-footer">
          {createdKey ? (
            <button className="btn-primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleCreate}
                disabled={!name.trim() || permissions.length === 0 || submitting}
              >
                {submitting ? 'Creating...' : 'Create Key'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
